import { motion } from 'framer-motion';
import { Mail, Phone, AlertTriangle } from 'lucide-react';
import { ConfidenceBar } from '@/components/profiler/ConfidenceBar';
import type { DossierSectionProps } from './types';
import type { IdentifierVariant } from '@/types/profiler';

// ── risk indicator ─────────────────────────────────────────────────────────────

function RiskIndicator({ value }: { value: 'HIGH' | 'MEDIUM' | 'LOW' }) {
  const colors: Record<string, string> = { HIGH: '#FF1F1F', MEDIUM: '#F0B429', LOW: '#00C853' };
  const c = colors[value] ?? '#5A7A9A';
  return (
    <span
      style={{
        background: `${c}15`,
        color: c,
        border: `1px solid ${c}30`,
        fontSize: 8,
        fontFamily: 'monospace',
        fontWeight: 700,
        padding: '1px 6px',
        borderRadius: 99,
      }}
    >
      {value}
    </span>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

export function Contacts({ report, pipeline }: DossierSectionProps) {
  const cluster = pipeline?.cluster ?? report.identifier_cluster;
  const ato = pipeline?.ato;

  const emailVariants: IdentifierVariant[] = cluster?.variants.filter(v => v.type === 'EMAIL') ?? [];
  const phoneVariants: IdentifierVariant[] = cluster?.variants.filter(v => v.type === 'PHONE') ?? [];

  // Deduplicate by value
  const emailsDeduped = emailVariants.filter((v, i, arr) => arr.findIndex(x => x.value === v.value) === i);
  const phonesDeduped = phoneVariants.filter((v, i, arr) => arr.findIndex(x => x.value === v.value) === i);

  // Determine email risk from ATO level
  const atoLevel = ato?.risk_level?.value ?? null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-5"
    >
      {/* ATO breach note */}
      {ato && (ato.breach_count ?? 0) > 0 && (
        <div
          style={{
            background: '#1A0E00',
            border: '1px solid #F0B42930',
            borderRadius: 8,
            padding: '10px 14px',
          }}
          className="flex items-center gap-3"
        >
          <AlertTriangle size={14} style={{ color: '#F0B429', flexShrink: 0 }} />
          <span style={{ color: '#F0B429', fontSize: 10, fontFamily: 'monospace' }}>
            {ato.breach_count} breach source{(ato.breach_count ?? 0) > 1 ? 's' : ''} detected —
            email addresses may be exposed in dark-web credential databases
          </span>
        </div>
      )}

      {/* Email section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Mail size={12} style={{ color: '#2979FF' }} />
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em' }}>
            EMAIL ADDRESSES ({emailsDeduped.length})
          </p>
        </div>

        {emailsDeduped.length > 0 ? (
          <div
            style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, overflow: 'hidden' }}
          >
            {/* Table header */}
            <div
              className="grid gap-3 px-4 py-2"
              style={{
                gridTemplateColumns: '1fr 100px 80px 60px',
                background: '#090D13',
                borderBottom: '1px solid #182030',
              }}
            >
              {['EMAIL ADDRESS', 'SOURCE', 'CONFIDENCE', 'RISK'].map(h => (
                <span key={h} style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.1em' }}>
                  {h}
                </span>
              ))}
            </div>
            {emailsDeduped.map((v, i) => (
              <div
                key={i}
                className="grid gap-3 px-4 py-2.5 items-center"
                style={{
                  gridTemplateColumns: '1fr 100px 80px 60px',
                  borderBottom: i < emailsDeduped.length - 1 ? '1px solid #0F1928' : undefined,
                }}
              >
                <span style={{ color: '#DCE8F5', fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {v.value}
                </span>
                <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {v.source}
                </span>
                <ConfidenceBar value={v.confidence} color="#2979FF" height={4} showLabel />
                <RiskIndicator value={atoLevel ?? 'LOW'} />
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '24px', textAlign: 'center' }}
          >
            <span style={{ color: '#283A50', fontSize: 10, fontFamily: 'monospace' }}>
              No email addresses found
            </span>
          </div>
        )}
      </div>

      {/* Phone section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Phone size={12} style={{ color: '#00C853' }} />
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em' }}>
            PHONE NUMBERS ({phonesDeduped.length})
          </p>
        </div>

        {phonesDeduped.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {phonesDeduped.map((v, i) => (
              <div
                key={i}
                style={{
                  background: '#0C1118',
                  border: '1px solid #182030',
                  borderLeft: '3px solid #00C853',
                  borderRadius: 8,
                  padding: '10px 12px',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>
                    {v.value}
                  </span>
                  <span
                    style={{
                      background: '#00C85318',
                      color: '#00C853',
                      border: '1px solid #00C85330',
                      fontSize: 8,
                      fontFamily: 'monospace',
                      fontWeight: 700,
                      padding: '1px 6px',
                      borderRadius: 99,
                    }}
                  >
                    PHONE
                  </span>
                </div>
                <ConfidenceBar value={v.confidence} color="#00C853" height={3} showLabel />
                <div style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', marginTop: 4 }}>
                  {v.source}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '24px', textAlign: 'center' }}
          >
            <span style={{ color: '#283A50', fontSize: 10, fontFamily: 'monospace' }}>
              No phone numbers found
            </span>
          </div>
        )}
      </div>

      {/* Data source note */}
      <div
        style={{
          background: '#060E1A',
          border: '1px solid #1E2B3F',
          borderRadius: 8,
          padding: '10px 14px',
        }}
      >
        <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', lineHeight: 1.6 }}>
          Data sourced from identity registries, breach records, and open-source enumeration.
          Confidence scores reflect source reliability and cross-correlation. Breach-derived records
          should be independently verified before use in regulatory filings.
        </p>
      </div>
    </motion.div>
  );
}
